import "./AutolinkListDialog.scss";

import { Alert, Button, Flex, Popup, Text } from "@fluentui/react-northstar";
import { getImageCardUrl } from "@teams/cards";
import { TeamsApplicationTypes, TeamsIntegrationReferenceType } from "@teams/types";
import { IntegrationAutoLinkDTO, ScheduleDTO, SurveyDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { useAutolinkIntegrations } from "../../actions/useAutolinkIntegration";
import { useUserInfo } from "../../actions/useUserInfo";
import { isError } from "../../services/ChannelApi";
import { integrationsApi } from "../../services/IntegrationsApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { IntegrationSystemIcon } from "../integrations/IntegrationSystemIcon";
import { PollyDialog } from "../PollyDialog";
import { PollyIcon } from "../PollyIcon";
import { TeamsLoader } from "../TeamsLoader";
import { AutolinkAddEditDialog } from "./AutolinkAddEditDialog";

type AutolinkListDialogParams = {
  reference: ScheduleDTO | SurveyDTO;
  referenceType: TeamsIntegrationReferenceType;
  setIsOpen: (isOpen: boolean) => void;
  onChanged: () => void;
};
export const AutolinkListDialog: React.FC<AutolinkListDialogParams> = ({
  reference,
  referenceType,
  setIsOpen,
  onChanged,
}) => {
  const { teamsContext } = useTeamsContext();
  const {
    autolinkIntegrations,
    autolinkIntegrationsError,
    isLoadingAutolinkIntegrations,
    loadIntegrations,
  } = useAutolinkIntegrations(reference.id as string, referenceType);
  const { user } = useUserInfo();

  const [isAddEditAutolinkOpen, setIsAddEditAutolinkOpen] = useState<boolean>(false);
  const [editAutolinkIntegration, setAutolinkIntegration] = useState<IntegrationAutoLinkDTO>();
  const [deleteIntegrationError, setDeleteIntegrationError] = useState<Error>();

  useEffect(() => {
    loadIntegrations(teamsContext);
  }, [teamsContext, loadIntegrations]);

  const deleteIntegration = async (id: string) => {
    setDeleteIntegrationError(undefined);

    const result = await integrationsApi.deleteIntegration(teamsContext, id);
    if (isError(result)) {
      setDeleteIntegrationError(result);
    }

    await loadIntegrations(teamsContext);
  };

  const editIntegration = (integration: IntegrationAutoLinkDTO) => {
    setAutolinkIntegration(integration);
    setIsAddEditAutolinkOpen(true);
  };

  return (
    <PollyDialog
      open
      onCancel={() => {
        setIsOpen(false);
        onChanged();
      }}
      header="Task Management Reference"
    >
      <div className="autolink-list">
        <Text>
          References to external task management apps like Jira, will be automatically converted
          into links in your AgilePolly results. This requires that the reference follows a
          PREFIX-NUMBER format.
        </Text>
        <Flex vAlign="center">
          <Flex.Item grow>
            <Text weight="bold" as="h3">
              Task Management References
            </Text>
          </Flex.Item>
          <Button
            primary
            onClick={() => {
              setAutolinkIntegration(undefined);
              setIsAddEditAutolinkOpen(true);
              clientEventTracker.track("add task clicked", user, {
                upn: user.upn,
              });
            }}
          >
            <PollyIcon inheritColor icon="add-choice-icon" label="Add Task Manager" />
          </Button>
        </Flex>

        {deleteIntegrationError && <Alert danger content={deleteIntegrationError.message} />}

        <TeamsLoader
          isLoading={isLoadingAutolinkIntegrations}
          loadingMessage="Loading ingegrations ..."
        >
          {autolinkIntegrationsError && (
            <Alert danger content={autolinkIntegrationsError.message} />
          )}

          {(!autolinkIntegrations || autolinkIntegrations.length === 0) && (
            <NoResults applicationType={reference.applicationType} />
          )}

          {autolinkIntegrations && autolinkIntegrations.length > 0 && (
            <div className="autolink-list__table-wrapper">
              <table className="autolink-list__table">
                <thead className="autolink-list__list-header">
                  <tr>
                    <td>Type</td>
                    <td>Prefix</td>
                    <td></td>
                    <td>URL</td>
                    <td></td>
                    <td></td>
                  </tr>
                </thead>
                <tbody className="autolink-list__list">
                  {autolinkIntegrations.map(integration => (
                    <IntegrationRow
                      key={`${integration.system}-${integration.prefix}`}
                      integration={integration}
                      editIntegration={editIntegration}
                      deleteIntegration={deleteIntegration}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TeamsLoader>

        {isAddEditAutolinkOpen && (
          <AutolinkAddEditDialog
            applicationType={reference.applicationType}
            integration={editAutolinkIntegration}
            referenceType={referenceType}
            referenceId={reference.id as string}
            setIsOpen={() => {
              setIsAddEditAutolinkOpen(false);
              loadIntegrations(teamsContext);
            }}
          />
        )}
      </div>
    </PollyDialog>
  );
};

const IntegrationRow: React.FC<{
  integration: IntegrationAutoLinkDTO;
  editIntegration: (integration: IntegrationAutoLinkDTO) => void;
  deleteIntegration: (id: string) => void;
}> = ({ integration, editIntegration, deleteIntegration }) => {
  const [confirmDelete, setConfirmDelete] = useState<boolean>();
  return (
    <tr>
      <td className="autolink-list__cell">
        <IntegrationSystemIcon system={integration.system} />
      </td>
      <td className="autolink-list__cell">
        <Text>{integration.prefix}</Text>
      </td>
      <td className="autolink-list__cell">
        <PollyIcon icon="arrow-icon" inheritColor className="autolink-list__arrow" />
      </td>
      <td className="autolink-list__cell autolink-list__url-cell">
        <Text>{integration.url}</Text>
      </td>
      <td className="autolink-list__cell autolink-list__icon-cell">
        <Button text iconOnly onClick={() => editIntegration(integration)}>
          <PollyIcon icon="edit-icon" />
        </Button>
      </td>
      <td className="autolink-list__cell autolink-list__icon-cell">
        <Popup
          open={confirmDelete}
          onOpenChange={(_e, props) => setConfirmDelete(props?.open)}
          content={
            <Flex column style={{ maxWidth: "300px" }} gap="gap.small">
              <Alert
                warning
                content={
                  <>
                    Are you sure you want to delete the integration:
                    <IntegrationSystemIcon system={integration.system} />
                  </>
                }
              />
              <Button
                data-tname="confirm-delete-autolink"
                content="Confirm Remove"
                fluid
                primary
                onClick={() => {
                  deleteIntegration(integration.id);
                  setConfirmDelete(false);
                }}
              />
            </Flex>
          }
        >
          <Button
            className="trash-icon-button"
            text
            iconOnly
            data-tname="intiate-delete-autolink"
            onClick={() => {
              setConfirmDelete(true);
            }}
          >
            <PollyIcon icon="trash-icon" />
          </Button>
        </Popup>
      </td>
    </tr>
  );
};

const NoResults: React.FC<{
  applicationType: TeamsApplicationTypes;
}> = ({ applicationType }) => {
  return (
    <div className="autolink-list__missing-container">
      <Text size="larger" weight="bold" as="div" align="center">
        Nothing here!
      </Text>

      <Text size="medium" as="div" align="center">
        Click "Add Task Manager" to get started
      </Text>

      <img
        className="autolink-list__missing-item"
        src={getImageCardUrl("EMPTY", applicationType)}
        alt="No templates yet"
      ></img>
    </div>
  );
};
